<template lang="pug">
.pa-4.text-center
    div(ref='chart')
</template>

<script lang="ts">
import * as d3 from 'd3';
import { defineComponent } from 'vue';

// Helper function for calculating the middle angle of a pie slice
function midAngle(d: d3.DefaultArcObject): number {
    return d.startAngle + (d.endAngle - d.startAngle) / 2;
}

export default defineComponent({
    name: 'DonutGraph',
    props: {
        // Each element of the data array is an object with a name and value property
        // determined by the item-name and item-value props
        data: {
            type: Array,
            required: true,
        },
        itemName: {
            type: String,
            required: true,
        },
        itemValue: {
            type: String,
            required: true,
        },
        // The rest of the props are optional
        valueFormatter: {
            type: Function,
            default: (value: number) => {
                return value.toLocaleString();
            },
        },
        width: {
            type: Number,
            default: 550,
        },
        height: {
            type: Number,
            default: 350,
        },
        innerRadiusProportion: {
            type: Number,
            default: 0.65,
        },
        labelRadiusProportion: {
            type: Number,
            default: 1,
        },
        colors: {
            type: Array,
            default: () => d3.schemeTableau10,
        },
        labelFontSize: {
            type: Number,
            default: () => 16,
        },
        dataCy: {
            type: String,
            default: () => 'donut-graph',
        },
    },
    mounted() {
        this.renderChart();
    },
    methods: {
        getChartSvg() {
            d3.select(this.$refs.chart).select('svg').remove();
            return d3
                .select(this.$refs.chart)
                .append('svg')
                .attr('width', this.width)
                .attr('height', this.height)
                .attr('viewBox', [-this.width / 2, -this.height / 2, this.width, this.height]);
        },
        renderChart: function () {
            const radius = this.width / 4;
            const width = this.width;

            // Another arc that won't be drawn. Just for label positioning
            const outerArc: d3.Arc<any, d3.DefaultArcObject> = d3
                .arc()
                .innerRadius(radius)
                .outerRadius(radius);

            const pie: d3.Pie<any, any> = d3
                .pie()
                .padAngle(0.005)
                .sort(null)
                .value((d) => d[this.itemValue]);

            const color: d3.ScaleOrdinal<any, any> = d3
                .scaleOrdinal()
                .domain(this.data.map((d) => d[this.itemName]))
                .range(this.colors);

            const svg = this.getChartSvg();

            const dataReady = pie(this.data);

            // Text labels
            const labels = svg
                .append('g')
                .classed('graph-bar-labels', true)
                .selectAll('text')
                .data(dataReady)
                .join('text')
                .attr('transform', function (d: d3.DefaultArcObject): string {
                    const pos = outerArc.centroid(d);
                    const midangle = midAngle(d);
                    pos[0] = midangle < Math.PI ? width / 2 : -width / 2;
                    return `translate(${pos})`;
                })
                .style('text-anchor', (d) => {
                    const midangle = midAngle(d);
                    return midangle < Math.PI ? 'end' : 'start';
                })
                .call((text) => {
                    text.append('tspan')
                        .attr('font-weight', 'bold')
                        .attr('y', '0.3em')
                        .text((d) => d.data[this.itemName])
                        .text((d) => {
                            // Check if the cost is not equal to 0 before displaying the value
                            if (d.data[this.itemValue] !== 0) {
                                return d.data[this.itemName];
                            } else {
                                return ''; // Empty string for $0 cost
                            }
                        });
                })
                .call((text) => {
                    text.append('tspan')
                        .attr('x', 0)
                        .attr('y', '1.5em')
                        .attr('fill-opacity', 0.7)
                        .text((d) => {
                            // Check if the cost is not equal to 0 before displaying the value
                            if (d.data[this.itemValue] !== 0) {
                                return this.valueFormatter(d.data[this.itemValue]);
                            } else {
                                return ''; // Empty string for $0 cost
                            }
                        });

                    // .call((text) => {
                    //     text.append('tspan')
                    //         .attr('x', 0)
                    //         .attr('y', '1.5em')
                    //         .attr('fill-opacity', 0.7)
                    //         .text((d) => this.valueFormatter(d.data[this.itemValue]));
                });
            // Calculate the maximum radius of the pie that won't interfere with the labels
            // from the length of the text
            // but take into account that some text is higher or lower than
            // the center of the horizontal axis of the pie chart,
            // and therefore has more horizontal room
            const fitRadius = Math.min(
                d3.min(labels, (n) => {
                    // A bit of a hacky way to get the vertical position of the label
                    const y = n.transform.baseVal.getItem(0).matrix.f;
                    return Math.hypot(width / 2 - n.getComputedTextLength(), y) - 20;
                }),
                this.height / 2,
            );

            const arc: d3.Arc<any, d3.DefaultArcObject> = d3
                .arc()
                .innerRadius(fitRadius * this.innerRadiusProportion)
                .outerRadius(fitRadius);

            // Draw the arcs
            svg.selectAll('path')
                .data(dataReady)
                .join('path')
                .attr('fill', (d) => color(d.data[this.itemName]))
                .attr('d', arc as unknown as string)
                .on('mouseover', function () {
                    d3.select(this)

                        .attr('stroke', '#F8F8F8')
                        .attr('stroke-width', '1');
                })
                .on('mouseout', function () {
                    d3.select(this).attr('stroke', 'none');
                });

            // Add the polylines between chart and labels:
            // Another arc that won't be drawn. Just for label positioning
            const elbowArc: d3.Arc<any, d3.DefaultArcObject> = d3
                .arc()
                .innerRadius(fitRadius * 0.9)
                .outerRadius(fitRadius * 0.9);

            svg.append('g')
                .selectAll('allPolylines')
                .data(dataReady)
                .join('polyline')
                .attr('stroke', 'white')
                .style('fill', 'none')
                .attr('stroke-width', 1)
                .attr('points', function (d: d3.DefaultArcObject, i) {
                    if (d.data.total !== 0) {
                        const textLength = labels.nodes()[i].getComputedTextLength() - 10;

                        const posA = arc.centroid(d); // line insertion in the slice
                        const posB = elbowArc.centroid(d); // line break: we use the other arc generator that has been built only for that
                        const posC = outerArc.centroid(d); // Label position = almost the same as posB
                        const midangle = midAngle(d);
                        posB[1] = posC[1]; // The last segment should be horizontal
                        posC[0] = (width / 2 - textLength) * (midangle < Math.PI ? 1 : -1); // multiply by 1 or -1 to put it on the right or on the left
                        return [posA, posB, posC];
                    } else {
                        return ''; // Empty string for $0 cost
                    }
                });

            return svg.node();
        },
    },
});
</script>
